// ============================================================================
// STRATA PLAN NUMBER NORMALIZER  (QW-SP-CLIENT-1)
// ----------------------------------------------------------------------------
// Extracts + normalizes a NSW strata plan number to the canonical display form
// "SP 32224" from the many shapes the upstream data can carry. Customer Zero
// (RJ Bird) quotes for owners-corporation / strata jobs, so the SP number is a
// load-bearing line in the quote Client section — but the source field
// (formData.strataplan) and the project reference can hold the number in any of
// these formats, and the old inline `/SP\s*\d+/i` regex only matched the
// "SP32224" / "SP 32224" shapes, silently dropping the rest:
//
//   SP32224            -> "SP 32224"
//   SP 32224           -> "SP 32224"
//   S.P. 32224         -> "SP 32224"
//   sp32224            -> "SP 32224"
//   Strata Plan No. 32224  -> "SP 32224"
//   Strata Plan No 32224   -> "SP 32224"
//   Strata Plan 32224      -> "SP 32224"
//   Plan No 32224          -> "SP 32224"
//   Plan 32224             -> "SP 32224"
//   32224              -> "SP 32224"   (BARE numeric — only when the whole
//                                        field is digits; see safety note)
//
// SAFETY — do NOT grab a number out of a free-text address. This helper is
// applied ONLY to the strata field + the SP-bearing portion of projectRef,
// NEVER to formData.address. The bare-numeric rule therefore only fires when
// the ENTIRE trimmed input is digits (3-7 of them). A street address such as
// "54 Raglan Street, Eastwood NSW 2122" has words/commas, so the leading "54"
// (and the postcode "2122") are never mistaken for an SP number.
//
// Returns '' for empty / null / "N/A" / "-" / a string with no extractable
// strata number, so callers can fall through to the next source.
//
// Written as a TypeScript util (Vite handles .ts natively) per the frontend
// modernisation conventions (initials.ts pilot). Pure + side-effect-free so it
// is trivially unit-testable and reusable from any render branch.
// ============================================================================

// Australian strata plan numbers are short integers. Anchor a sane width so we
// don't promote a phone number / ABN fragment to an SP. 3-7 digits comfortably
// covers every real plan number while rejecting long numeric noise.
const SP_DIGIT_GROUP = /(\d{3,7})/;

// Phrasings that PRECEDE the digits and unambiguously mean "strata plan".
// Ordered longest-first so e.g. "Strata Plan No." is consumed before the
// shorter "Plan" fragment. The trailing part allows an optional "No"/"No."
// and optional punctuation/whitespace before the number.
const SP_PREFIX_PATTERNS: RegExp[] = [
    // S.P. / SP / S P  (with optional dots / spaces), optionally "No"
    /\bs\.?\s*p\.?\s*(?:no\.?\s*)?#?\s*(\d{3,7})\b/i,
    // Strata Plan [No] 32224
    /\bstrata\s*plan\s*(?:no\.?\s*)?#?\s*(\d{3,7})\b/i,
    // Plan [No] 32224
    /\bplan\s*(?:no\.?\s*)?#?\s*(\d{3,7})\b/i,
];

// Tokens that explicitly mean "no value" — short-circuit to ''.
const EMPTY_TOKENS = new Set(['', 'n/a', 'na', 'none', 'nil', 'tbc', 'tba', '-', '—', '?']);

/**
 * Normalize any strata-plan-bearing string to the canonical "SP <number>" form.
 *
 * @param raw  The source string (formData.strataplan, or an SP-bearing slice of
 *             a project reference). null / undefined / non-string -> ''.
 * @returns    "SP 32224" style string, or '' when no strata number is present.
 */
export function normalizeStrataPlanNumber(raw: string | null | undefined): string {
    if (raw === null || raw === undefined) return '';
    const text = String(raw).trim();
    if (!text) return '';
    if (EMPTY_TOKENS.has(text.toLowerCase())) return '';

    // 1. Prefixed forms (SP / S.P. / Strata Plan [No] / Plan [No]).
    for (const pattern of SP_PREFIX_PATTERNS) {
        const m = text.match(pattern);
        // The capture group is provably defined when `m` is truthy (the pattern
        // requires \d{3,7}); the `!` satisfies noUncheckedIndexedAccess.
        if (m) return `SP ${m[1]!}`;
    }

    // 2. Bare numeric — ONLY when the entire field is just digits (optionally
    //    wrapped in incidental whitespace). This is the deliberate guard that
    //    keeps a free-text address ("54 Raglan Street, ... 2122") from being
    //    mistaken for a plan number: such a string has non-digit chars and so
    //    fails this whole-string test.
    if (/^\d{3,7}$/.test(text)) {
        return `SP ${text}`;
    }

    // 3. Last resort: a "#32224"-style field that is only a hash + digits.
    const hashOnly = text.match(/^#\s*(\d{3,7})$/);
    if (hashOnly) return `SP ${hashOnly[1]!}`;

    return '';
}

/**
 * Pick the SP-bearing portion of a project reference, then normalize it.
 *
 * projectRef can be a pipe-delimited composite (e.g.
 * "SP 32224 | The Owners | 54 Raglan St"). We isolate the part that looks like
 * a strata plan before normalizing so we never accidentally read a number out
 * of the address segment of the composite.
 *
 * @param projectRef  The raw header.projectRef string.
 * @returns           Canonical "SP <number>" or ''.
 */
export function strataPlanFromProjectRef(projectRef: string | null | undefined): string {
    if (!projectRef) return '';
    const text = String(projectRef);

    if (text.includes('|')) {
        const parts = text.split('|').map((p) => p.trim());
        // Prefer a part that explicitly looks like a strata plan reference.
        for (const part of parts) {
            if (/\b(?:s\.?\s*p\.?|strata\s*plan|plan)\b/i.test(part)) {
                const normalized = normalizeStrataPlanNumber(part);
                if (normalized) return normalized;
            }
        }
        // No explicit-prefix part matched: do NOT fall back to a bare-numeric
        // scan of the composite (that risks grabbing a postcode from the
        // address segment).
        return '';
    }

    // Non-composite projectRef: try to lift a prefixed SP token out of the
    // free text (e.g. "Quote for SP 32224 works"). Reuse the prefix patterns
    // but never the bare-numeric whole-string rule (the projectRef is prose).
    for (const pattern of SP_PREFIX_PATTERNS) {
        const m = text.match(pattern);
        if (m) return `SP ${m[1]!}`;
    }
    return '';
}
